import { useState } from 'react'
import type { PurchaseOrderEventFormValues } from '@klear/purchase-order/wire.client'
import {
  purchaseOrderEventFormToCreateWire,
  purchaseOrderEventFormToUpdateWire,
  validatePurchaseOrderEventForm
} from '@klear/purchase-order/wire.client'
import { trpc } from '@klear/ui/lib/trpc'
import { PurchaseOrderForm } from '@klear/order-event-ui/event-forms/purchase-order-form'
import { EventFormShell, type EventFormHandlerProps } from '@klear/order-event-ui/event-shared/event-form-shell'
import { useEventFormState } from '@klear/order-event-ui/event-shared/event-form-state'
import { useEventFormSave, type RunEventCommit } from '@klear/order-event-ui/event-shared/use-event-form-save'

export function PurchaseOrderEventForm({ bootstrap, orderId, traderId, onSuccess, onCancel }: EventFormHandlerProps) {
  const formState = useEventFormState<PurchaseOrderEventFormValues>({
    initial: bootstrap.formValues as PurchaseOrderEventFormValues,
    validate: validatePurchaseOrderEventForm
  })
  const [isLoading, setIsLoading] = useState(false)
  const { save } = useEventFormSave()
  const create = trpc.purchaseOrder.create.useMutation()
  const update = trpc.purchaseOrder.update.useMutation()

  const handleSubmit = async (data: PurchaseOrderEventFormValues) => {
    setIsLoading(true)
    try {
      const runCommit: RunEventCommit = async ({ id }) => {
        if (id == null) {
          const result = await create.mutateAsync({
            traderId,
            data: purchaseOrderEventFormToCreateWire(data, orderId)
          })
          return { id: result.id }
        }
        const result = await update.mutateAsync({
          id,
          traderId,
          data: purchaseOrderEventFormToUpdateWire(data)
        })
        return { id: result.id }
      }
      await save({
        traderId,
        eventId: bootstrap.editEventId,
        keptDocumentIds: (data.documents ?? []).map((document) => document.id),
        originalDocumentIds: bootstrap.originalDocumentIds,
        files: (data.files ?? []).filter((f): f is File => f instanceof File),
        runCommit
      })
      onSuccess?.()
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <EventFormShell
      formState={formState}
      title={`${bootstrap.editEventId != null ? 'Edit' : 'Add'} Purchase Order`}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      onCancel={onCancel}
    >
      <PurchaseOrderForm />
    </EventFormShell>
  )
}
